import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  IconButton,
  Slide,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { urls } from "../../../urls/urls";
import { BASE_URL_TEST } from "../../../utils/baseUrl";
import {
  getAppToken,
  getUserDetailsAction,
  getLoginToken,
  getVendorDetails,
} from "../../../store/actions/userActions";

import { useDispatch, useSelector } from "react-redux";
import { useFetch } from "../../../hooks";

// This route only ever handles the Vendor login flow.
// Admin login lives at a completely separate route/component (AdminLogin.jsx)
// so the two flows never share a URL, a bundle-level toggle, or state.
const VendorLogin = () => {
  const navigate = useNavigate();
  const appStateToken = useSelector((state) => state.user.apptoken);
  const vendorIdData = useSelector((state) => state.user.vendordata);
  const dispatch = useDispatch();

  const {
    data: tokenData,
    error: tokenError,
    fetchData: TokenFetchData,
  } = useFetch(BASE_URL_TEST + urls("generateToken"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });

  const [vendorId, setVendorId] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showSnackBar, setShowSnackBar] = useState({
    showsnack: false,
    message: "",
  });

  const [isTyping, setIsTyping] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [isWrong, setIsWrong] = useState(false);
  const targetVendorId = "159753";

  const {
    data: loginData,
    error: loginError,
    fetchData: LoginFetchData,
  } = useFetch(BASE_URL_TEST + urls("vlogin"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: appStateToken ? `Bearer ${appStateToken}` : "",
    },
  });

  const {
    data: verifyVendorData,
    error: verifyVendorError,
    fetchData: verifyVendoretchData,
  } = useFetch(BASE_URL_TEST + urls("verifyVendor"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: appStateToken ? `Bearer ${appStateToken}` : "",
    },
  });

  useEffect(() => {
    TokenFetchData({
      grant_type: "password",
      username: "Admin",
      password: "QWRtaW5AITQzMQ==",
    });
  }, []);

  useEffect(() => {
    if (tokenError) {
    } else if (tokenData && tokenData["status"] === true) {
      dispatch(getAppToken(tokenData["token"]));
    }
  }, [tokenData, tokenError]);

  useEffect(() => {
    if (loginError) {
      setSnackbarSeverity("error");
      setShowSnackBar({
        ...showSnackBar,
        showsnack: true,
        message: "Failed to login",
      });
    } else if (loginData && loginData["status"] === true) {
      setSnackbarSeverity("success");
      dispatch(getUserDetailsAction(loginData["data"]));
      dispatch(getLoginToken(loginData["token"]));
      setShowSnackBar({
        ...showSnackBar,
        showsnack: true,
        message: loginData["message"],
      });
      setTimeout(() => {
        navigate("/vendor-dashboard");
      }, 2000);
    } else if (loginData && loginData["status"] === false) {
      setSnackbarSeverity("error");
      setShowSnackBar({
        ...showSnackBar,
        showsnack: true,
        message: loginData["message"],
      });
    }
  }, [loginData, loginError]);

  useEffect(() => {
    if (vendorId.length === 0) {
      setIsTyping(false);
      setIsCompleted(false);
      setIsWrong(false);
      setProgress(0);
    } else if (vendorId !== targetVendorId) {
      setIsWrong(true);
      setIsTyping(false);
      setIsCompleted(false);
      setProgress(0);
    } else if (vendorId === targetVendorId) {
      setIsCompleted(true);
      setIsTyping(false);
      setIsWrong(false);
      setProgress(100);
    }
  }, [vendorId]);

  useEffect(() => {
    if (verifyVendorError) {
      console.log("verifyVendor error", verifyVendorError);
    } else if (verifyVendorData && verifyVendorData["status"] === true) {
      setIsCompleted(true);
      setIsWrong(false);
      setSnackbarSeverity("success");
      dispatch(getVendorDetails(verifyVendorData["data"]));
      setShowSnackBar({
        ...showSnackBar,
        message: "vendor verified",
        showsnack: true,
      });
    } else if (verifyVendorData && verifyVendorData["status"] === false) {
      setSnackbarSeverity("error");
      setShowSnackBar({
        ...showSnackBar,
        message: verifyVendorData["message"],
        showsnack: true,
      });
    }
  }, [verifyVendorData, verifyVendorError]);

  const handleLogin = () => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@gmail\.com$/i;

    if (!vendorId || vendorId.length !== 10) {
      setSnackbarSeverity("error");
      setShowSnackBar({
        ...showSnackBar,
        showsnack: true,
        message: "Please enter a valid 10-digit Vendor ID",
      });
      return;
    }

    if (!email) {
      setSnackbarSeverity("error");
      setShowSnackBar({
        ...showSnackBar,
        showsnack: true,
        message: "Email Address is required",
      });
      return;
    }

    if (!emailRegex.test(email)) {
      setSnackbarSeverity("error");
      setShowSnackBar({
        ...showSnackBar,
        showsnack: true,
        message: "Please provide a valid Email Address",
      });
      return;
    }

    if (!password) {
      setSnackbarSeverity("error");
      setShowSnackBar({
        ...showSnackBar,
        showsnack: true,
        message: "Password is required",
      });
      return;
    }

    if (!isCompleted) {
      setSnackbarSeverity("error");
      setShowSnackBar({
        ...showSnackBar,
        showsnack: true,
        message: "Vendor ID is not verified yet",
      });
      return;
    }

    LoginFetchData({
      id: vendorIdData["id"],
      vendor_id: vendorId,
      email: email,
      password: password,
      device_type: "web",
    });
  };

  const handleVendorId = (value) => {
    if (value) {
      verifyVendoretchData({ vendor_id: value });
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setShowSnackBar({ ...showSnackBar, showsnack: false });
  };

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background:
          "linear-gradient(to bottom right, #2986cc, #4ea8de, #f97300)",
      }}
    >
      <Snackbar
        open={showSnackBar.showsnack}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={Slide}
      >
        <MuiAlert severity={snackbarSeverity} sx={{ width: "100%" }}>
          {showSnackBar.message}
        </MuiAlert>
      </Snackbar>
      <Card
        sx={{
          width: "35vw",
          padding: 4,
          boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.2)",
          borderRadius: "20px",
          backgroundColor: "#fff",
        }}
      >
        <CardContent>
          <Box sx={{ textAlign: "center", marginBottom: 4 }}>
            <Box
              component="img"
              src="/Sthanick.jpeg"
              alt="Sthanick"
              sx={{ height: 40, width: "auto" }}
            />
            <Typography
              variant="body1"
              sx={{ fontSize: "1rem", color: "#666", marginTop: 1 }}
            >
              Welcome back! Please login to continue.
            </Typography>
          </Box>

          <Box
            sx={{
              marginBottom: 3,
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <TextField
              fullWidth
              label="Vendor Id"
              placeholder="Enter your Vendor Id"
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  "&:hover fieldset": { borderColor: "#4ea8de" },
                  "&.Mui-focused fieldset": { borderColor: "#2986cc" },
                },
              }}
              value={vendorId}
              onChange={(e) => setVendorId(e.target.value)}
              onBlur={(e) => handleVendorId(e.target.value)}
              inputMode="numeric"
            />

            {isTyping && (
              <Box sx={{ alignItems: "center", marginLeft: 2 }}>
                <CircularProgress size={15} />
              </Box>
            )}

            {isCompleted && (
              <Box sx={{ alignItems: "center", marginLeft: 2 }}>
                <IconButton color="success">
                  <CheckCircleIcon />
                </IconButton>
              </Box>
            )}

            {isWrong && (
              <Box sx={{ alignItems: "center", marginLeft: 2 }}>
                <IconButton color="error">
                  <CancelIcon />
                </IconButton>
              </Box>
            )}
          </Box>

          {isCompleted && (
            <>
              <Box sx={{ marginBottom: 3 }}>
                <TextField
                  fullWidth
                  label="Email/Username"
                  placeholder="Enter your email"
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      "&:hover fieldset": { borderColor: "#4ea8de" },
                      "&.Mui-focused fieldset": { borderColor: "#2986cc" },
                    },
                  }}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Box>

              <Box sx={{ marginBottom: 3 }}>
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  placeholder="Enter your password"
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      "&:hover fieldset": { borderColor: "#4ea8de" },
                      "&.Mui-focused fieldset": { borderColor: "#2986cc" },
                    },
                  }}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </Box>
            </>
          )}

          <Box sx={{ marginBottom: 3 }}>
            <Button
              fullWidth
              variant="contained"
              sx={{
                padding: "12px 0",
                background: "linear-gradient(to right, #4ea8de, #2986cc)",
                fontSize: "1rem",
                fontWeight: "bold",
                borderRadius: "10px",
                textTransform: "none",
                boxShadow: "0px 5px 15px rgba(46, 168, 222, 0.4)",
                "&:hover": {
                  background: "linear-gradient(to right, #2986cc, #f97300)",
                },
              }}
              onClick={handleLogin}
            >
              Login
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default VendorLogin;
